import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { Skeleton } from 'antd'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { Routes } from '../../../constants/Routes'
import { AppMainView, AppView } from '../../../components/AppView'
import AppHeader from '../../../components/AppView/Header'
import { HelpLinkMenu } from '../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { EmployeeFilter } from './EmployeeFilter'
import { EmployeeTable } from './EmployeeTable'
import { EmployeeDeleteModal } from './EmployeeDeleteModal'
import './AddEmployee.scss'
import { EmployeeFilterStore } from './EmployeeFilterStore'
import { IUserResponse, PermissionNames } from '@getgreenline/homi-shared'
import { browserHistory } from 'react-router'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { HelpLinks } from '../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  selectedEmployee: IUserResponse | undefined
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardEmployees extends React.Component<Props, State> {
  filter = new EmployeeFilterStore()
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      selectedEmployee: undefined,
    }
  }

  componentDidMount = async () => {
    if (!this.canManageEmployee) {
      return
    }

    const users = await this.props.currentCompanyStore.getEntityUsers()
    this.filter.setUsers(users)

    const securityRoles = await this.props.currentCompanyStore.getSecurityRoles()
    this.filter.setSecurityRoles(securityRoles)

    const locations = await this.props.currentCompanyStore.getLocations()
    this.filter.setLocations(locations)
  }

  removeEmployee = async () => {
    if (!this.state.selectedEmployee) {
      return
    }

    const users = await this.props.currentCompanyStore.deleteEntityUser(
      this.state.selectedEmployee.id,
    )
    this.filter.setUsers(users)
    this.filter.setFilteredUsers()
    this.toggleDeleteModal()
  }

  toggleDeleteModal = (user?: IUserResponse) => this.setState({ selectedEmployee: user })

  get canManageEmployee(): boolean {
    return this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_MANAGE_EMPLOYEES,
      this.company.id,
    )
  }

  render() {
    return this.canManageEmployee ? (
      <div className='full-view'>
        <AppView column>
          <AppHeader title='Employees'>
            <span className='spaced-button-wrapper'>
              <HelpLinkMenu
                helpLinkMenus={{
                  href: HelpLinks.EMPLOYEES,
                  title: 'Adding employees',
                }}
              />

              <BlazeButton
                buttonProps={{
                  icon: 'security-scan',
                  href: Routes.DASHBOARD_EMPLOYEES_SECURITY_ROLES(
                    this.props.currentCompanyStore.company!.id,
                  ),
                }}
                titleProps={{
                  title: 'View security roles',
                }}
              />

              <BlazeButton
                buttonProps={{
                  type: 'primary',
                  icon: 'plus',
                  onClick: () => {
                    const companyId = this.props.currentCompanyStore.company!.id
                    browserHistory.push(
                      Routes.DASHBOARD_ADD_EMPLOYEE(companyId, window.location.search),
                    )
                  },
                }}
                titleProps={{
                  title: 'Add employee',
                }}
              />
            </span>
          </AppHeader>

          <AppMainView className='background-grey intercom-pb-2'>
            <EmployeeFilter filter={this.filter} />
            {this.filter.isLoading ? (
              <Skeleton active />
            ) : (
              <EmployeeTable filter={this.filter} toggleDeleteModal={this.toggleDeleteModal} />
            )}

            {this.state.selectedEmployee && (
              <EmployeeDeleteModal
                visible={!!this.state.selectedEmployee}
                selectedEmployee={this.state.selectedEmployee}
                removeEmployee={this.removeEmployee}
                onCancel={this.toggleDeleteModal}
                isOwnEmployeeAccount={
                  this.props.currentUserStore?.currentUser?.id === this.state.selectedEmployee?.id
                }
              />
            )}
          </AppMainView>
        </AppView>
      </div>
    ) : (
      <NoPermissionsResult permissionName={PermissionNames.CAN_MANAGE_EMPLOYEES} />
    )
  }
}
